import axios from 'axios'

function check_target_type (target, state) {
  return !(target.type in state.targetTypes) || state.targetTypes[target.type]
}

function check_target_species (target, state) {
  return !(target.organism in state.targetSpecies) || state.targetSpecies[target.organism]
}

function target_has_affinity_data(targetid, assayDict) {
  for (let assayid in assayDict) {
    let assay = assayDict[assayid]
    if (assay.target === targetid) {
      if (!isNaN(parseFloat(assay.act_value))) {
        return true
      }
    }
  }
  return false
}

let makeTaskStore = function () {
  return {
    state: {
      task: {},
      minimalCompoundSimilarity: 0.1,
      hideNullCompounds: false,
      hideOwnTargets: false,
      targetTypes: { 'SINGLE PROTEIN': true },
      targetSpecies: { 'Homo sapiens': true },
      assayRange: {}
    },

    mutations: {
      set (state, params) {
        for (let k in params) {
          state[k] = params[k]
        }
      }
    },

    actions: {
      getTask ({commit, state, dispatch}, {taskid, icompound}) {
        return axios({
          method: 'get',
          url: `//${location.hostname}/dokhlab/actions/drift/task?taskid=${taskid}&icompound=${icompound}`,
          withCredentials: true
        }).then(response => {
          let task = response.data
          task.par = JSON.parse(task.par)
          task.result = JSON.parse(task.result)

          // set scores of compounds
          for (let compoundid in task.result.compounds) {
            let compound = task.result.compounds[compoundid]
            compound.fp2score = parseFloat(compound.fp2score)
            compound.pharmscore = 1 - Math.exp(-parseFloat(compound.pharmscore) / 2.0)
            compound.score = 0.2 * compound.fp2score + 0.2 * compound.pharmscore + 0.6 * Math.max(compound.fp2score, compound.pharmscore)
            // if (isNaN(compound.score)) {
            //   compound.score = 0
            // }
          }

          let ls = {}
          for (let assayid in task.result.assays) {
            let assay = task.result.assays[assayid]
            assay.act_value = parseFloat(assay.act_value)
            if (!isNaN(assay.act_value)) {
              ls[assayid] = assay
            }
          }
          task.result.assays = ls

          // set scores of targets
          ls = {}
          for (let targetid in task.result.targets) {
            let target = task.result.targets[targetid]
            target.score = parseFloat(target.score)
            if (isNaN(target.score)) {
              target.score = 0
            }
            if (target_has_affinity_data(targetid, task.result.assays)) {
              ls[targetid] = target
            }
          }
          task.result.targets = ls

          return task
        })
      },

      fetchTask ({commit, state, dispatch}, {taskid, icompound}) {
        return dispatch('getTask', {taskid, icompound}).then(task => {
          commit('set', {task})
        })
      }
    },

    getters: {
      par (state) {
        if (state.task) {
          return state.task.par
        } else {
          return {}
        }
      },

      compoundSmiles (state, getters) {
        if (getters.par.compound) {
          return getters.par.compound.split('\n').filter(line => line.trim() !== '')
        } else {
          return []
        }
      },

      filteredCompounds (state) {
        let r = {}
        if (state.task && state.task.result) {
          let compoundDict = state.task.result.compounds
          for (let compoundid in compoundDict) {
            let compound = compoundDict[compoundid]
            if (compound.fp2score >= state.minimalCompoundSimilarity) {
              r[compoundid] = compound
            }
          }
        }
        return r
      },

      filteredTargets (state) {
        let r = {}
        if (state.task && state.task.result) {
          let targetDict = state.task.result.targets
          for (let targetid in targetDict) {
            let target = targetDict[targetid]
            if (check_target_type(target, state) && check_target_species(target, state)) {
//            if (!(target.type in state.targetTypes) || state.targetTypes[target.type]) {
              if (target.pref_name !== 'No relevant target') {
                r[targetid] = target
              }
            }
          }
        }
        return r
      },

      filteredAssays (state, getters) {
        let r = {}
        if (state.task && state.task.result) {
          let targetDict = getters.filteredTargets
          let compoundDict = getters.filteredCompounds
          let assayDict = state.task.result.assays
          for (let assayid in assayDict) {
            let assay = assayDict[assayid]
            assay.act_value = parseFloat(assay.act_value)
            if (isNaN(assay.act_value)) {
              continue
            }
            if ((assay.target in targetDict) && (assay.compound in compoundDict)) {
              if (!state.hideOwnTargets || state.task.result.compounds[assay.compound].fp2score !== 1) {
                if (!state.assayRange[assay.act_type] || !state.assayRange[assay.act_type][assay.act_units] ||
                  !state.assayRange[assay.act_type][assay.act_units].active ||
                  (assay.act_value >= state.assayRange[assay.act_type][assay.act_units].range[0] &&
                    assay.act_value <= state.assayRange[assay.act_type][assay.act_units].range[1])) {
                  r[assayid] = assay
                }
              }
            }
          }
        }
        return r
      },

      compoundTargets (state, getters) {
        let r = {}
        let assayDict = getters.filteredAssays
        let compoundDict = getters.filteredCompounds
        for (let compoundid in compoundDict) {
          r[compoundid] = []
        }
        for (let assayid in assayDict) {
          let assay = assayDict[assayid]
          r[assay.compound].push(assay.target)
        }
        return r
      },

      targetCompounds (state, getters) {
        let r = {}
        let assayDict = getters.filteredAssays
        let targetDict = getters.filteredTargets
        for (let targetid in targetDict) {
          r[targetid] = []
        }
        for (let assayid in assayDict) {
          let assay = assayDict[assayid]
          r[assay.target].push(assay.compound)
        }
        return r
      },

      assays (state, getters) {
        return getters.filteredAssays
      },

      compounds (state, getters) {
        let r = {}
        let compoundDict = getters.filteredCompounds
        for (let compoundid in compoundDict) {
          let compound = compoundDict[compoundid]
          if (getters.compoundTargets[compoundid].length > 0 || !state.hideNullCompounds) {
            r[compoundid] = compound
          }
        }
        return r
      },

      targets (state, getters) {
        let r = {}
        if (state.task && state.task.result) {
          let targetDict = getters.filteredTargets
          let assayDict = getters.filteredAssays
          for (let assayid in assayDict) {
            let assay = assayDict[assayid]
            let target = targetDict[assay.target]
            if (!(assay.target in r)) {
              r[assay.target] = target
            }
          }
        }
        return r
      },

      targetIds (state, getters) {
        return Object.keys(getters.targets)
      },

      targetScores (state, getters) {
        let r = {}
        for (let targetid in getters.targets) {
          let target = getters.targets[targetid]
          let score = Math.max(...(getters.targetCompounds[targetid].map(compoundid => parseFloat(getters.compounds[compoundid].score))))
          r[targetid] = score * 0.8 + target.score * 0.2
        }
        return r
      },

      compoundIds (state, getters) {
        return Object.keys(getters.compounds)
      },

      processes (state, getters) {
        let r = {}
        if (state.task && state.task.result) {
          for (let targetid in getters.targets) {
            let target = getters.targets[targetid]
            for (let processid of target.processes) {
              if (!(processid in r)) {
                r[processid] = state.task.result.processes[processid]
              }
            }
          }
        }
        return r
      },

      functions (state, getters) {
        let r = {}
        if (state.task && state.task.result) {
          for (let targetid in getters.targets) {
            let target = getters.targets[targetid]
            for (let functionid of target.functions) {
              if (!(functionid in r)) {
                r[functionid] = state.task.result.functions[functionid]
              }
            }
          }
        }
        return r
      },

      fragments (state, getters) {
        let r = []
        if (state.task && state.task.result) {
          let fragmentArray = state.task.result.fragments.fragments
          let compoundArray = state.task.result.fragments.compounds
          let compoundSet = new Set()
          for (let fragment of fragmentArray) {
            if ((fragment.smi.match(/[A-Za-z]+/g) || []).length > 1) {
              let f = {
                smi: fragment.smi,
                source: []
              }
              let sourceSet = new Set()
              for (let c of fragment.source) {
                let compoundid = compoundArray[c.compound].name.split('-')[0]
                if (compoundid in getters.compounds) {
                  sourceSet.add(compoundid)
                  compoundSet.add(compoundid)
                }
              }
              f.source = [...sourceSet]
              if (f.source.length > 0) {
                r.push(f)
              }
            }
          }
          for (let f of r) {
            f.freq = f.source.length / compoundSet.size
          }
        }
        return r
      },

      allTargetTypes (state, getters) {
        let r = []
        if (state.task && state.task.result) {
          let targetDict = state.task.result.targets
          for (let targetid in targetDict) {
            let target = targetDict[targetid]
            let type = target.type
            if (!(type in r)) {
              r.push(type)
            }
          }
        }
        return r
      },

      allTargetSpecies (state, getters) {
        let r = []
        if (state.task && state.task.result) {
          let targetDict = state.task.result.targets
          for (let targetid in targetDict) {
            let target = targetDict[targetid]
            let species = target.organism
            if (!(species in r)) {
              r.push(species)
            }
          }
        }
        return r
      },

      targetNames (state, getters) {
        let stat = {}
        for (let targetid in getters.targets) {
          let target = getters.targets[targetid]
          let score = Math.max(...(getters.targetCompounds[targetid].map(compoundid => parseFloat(getters.compounds[compoundid].score))))
          if (!score) {
            score = 0
          }
          let val = target.pref_name
          if (val in stat) {
            stat[val] = Math.max(score, stat[val])
          } else {
            stat[val] = score * 0.8 + target.score * 0.2
          }
        }
        return stat
      },

      targetSpecies (state, getters) {
        let stat = {}
        for (let targetid in getters.targets) {
          let target = getters.targets[targetid]
          let val = target.organism
          if (val in stat) {
            stat[val] += 1
          } else {
            stat[val] = 1
          }
        }
        return stat
      },

      targetFunctions (state, getters) {
        let stat = {}
        for (let targetid in getters.targets) {
          let target = getters.targets[targetid]
          for (let functionid of target.functions) {
            let val = getters.functions[functionid].name
            if (val in stat) {
              stat[val] += 1
            } else {
              stat[val] = 1
            }
          }
        }
        return stat
      },

      targetProcesses (state, getters) {
        let stat = {}
        for (let targetid in getters.targets) {
          let target = getters.targets[targetid]
          for (let processid of target.processes) {
            let val = getters.processes[processid].name
            if (val in stat) {
              stat[val] += 1
            } else {
              stat[val] = 1
            }
          }
        }
        return stat
      },

      allAssayTypes (state, getters) {
        let r = []
        if (state.task && state.task.result) {
          let assayDict = state.task.result.assays
          for (let assayid in assayDict) {
            let assay = assayDict[assayid]
            let type = assay.act_type
            if (!(type in r)) {
              r[type] = {}
            }
            let units = assay.act_units
            let value = assay.act_value
            if (!(units in r[type])) {
              r[type][units] = []
            }
            r[type][units].push(value)
          }
        }
        return r
      }
    }
  }
}

export default makeTaskStore
